import React, { type FunctionComponent, useCallback, useEffect, useState } from 'react'
import { Prompt } from 'react-router-dom'
import {
  ActionSheetDropdown,
  DownloadIcon,
  SheetDropdown,
  SheetDropdownDisclosureBase,
} from '@instacart/ids-core'
import { css } from '@emotion/react'
import { Button, Portal, ActionSheetDropdownItem } from '@retailer-platform/shared-components'
import { type ProductSearchParams } from 'domains/products/components/search/utils/types'
import { useDownloadCatalogSearchResults } from 'domains/products/api/downloadCatalogSearchResultsMesh.hooks'
import { useGetSignedUrl } from 'domains/products/api/getCatalogSearchDownloadSignedUrl.hooks'
import { useDomainMessages } from 'domains/products/utils/domain/intl'
import { useNotifications } from 'legacy/common/utils/notifications/notifications.hooks'
import { validQuery } from 'domains/products/api/getRetailerProductsMesh.hooks'
import { SearchError } from 'domains/products/components/search/productSearch.constants'
import { useSelectionManager } from 'utils/selection-manager/SelectionManager'

const dropdownButtonStyle = css({
  backgroundColor: 'transparent',
})

const downloadReportButtonCss = css({
  display: 'flex',
  flexWrap: 'nowrap',
  justifyContent: 'center',
  alignItems: 'center',
  margin: 'auto',
  height: 40,
  marginLeft: 10,
  position: 'relative', // for Safari
})

const downloadReportSvgCss = css({
  display: 'block',
  paddingTop: 1,
  height: 25,
  fill: 'white',
  marginLeft: 5,
})

const selectedCountStyle = css({
  marginRight: 5,
})

interface ProductDownloadProps {
  searchParams: ProductSearchParams
}

// every ten seconds
const pollInterval = 10 * 1000
// ten minutes
const maxPoll = 10 * 60 * 1000
// 15 seconds
const errorNotificationTime = 15000

export const ProductsDownloadButton: FunctionComponent<ProductDownloadProps> = ({
  searchParams,
}) => {
  // imported hooks
  const { notify, notifyError } = useNotifications()
  const { selectedCount, selectedKeys } = useSelectionManager()

  // state
  const [triggerDownload, setTriggerDownload] = useState(false)
  const [pollCount, setPollCount] = useState(0)
  const [polling, setPolling] = useState(false)
  const [fileName, setFilename] = useState(undefined)
  const [requestProductData, setRequestProductData] = useState(true)

  // i18n
  const i18n = useDomainMessages({
    downloadProduct: 'productsDomain.search.download.product',
    downloadItem: 'productsDomain.search.download.item',
    downloadingConfirmation: 'productsDomain.search.download.confirmation',
    downloadingValidation: 'productsDomain.search.download.validation',
    downloadingError: 'productsDomain.search.download.error',
    downloadingTimeout: 'productsDomain.search.download.timeout',
    downloadingBlock: 'productsDomain.search.download.block',
  })

  const onDownload = useCallback(
    (requestingProductData = true) => {
      setRequestProductData(requestingProductData)

      const validation = validQuery(searchParams)
      if (!validation.isValid && validation.searchError != SearchError.TooManySearchTerms) {
        notifyError(i18n.downloadingValidation, 'modern', errorNotificationTime)
      } else {
        notify(i18n.downloadingConfirmation, 'modern')
        setPollCount(0)
        setTriggerDownload(true)
      }
    },
    [notify, notifyError, searchParams, i18n]
  )

  // trigger download
  const {
    apiResponse: catalogApiResponse,
    apiLoading: catalogApiLoading,
    apiError: catalogApiError,
  } = useDownloadCatalogSearchResults(
    {
      searchParams,
      requestProductData,
      selectedKeys,
    },
    !triggerDownload
  )

  // start polling
  if (
    !catalogApiLoading &&
    !catalogApiError &&
    catalogApiResponse &&
    'filename' in catalogApiResponse
  ) {
    setFilename(catalogApiResponse.filename)
    setTriggerDownload(false)
    setPolling(true)
  }
  if (catalogApiError) {
    setTriggerDownload(false)
    setPolling(false)

    notifyError(i18n.downloadingError, 'modern', errorNotificationTime)
  }

  // poll for signed url
  useGetSignedUrl(
    {
      s3Key: fileName,
    },
    !polling,
    polling ? pollInterval : 0,
    // on success
    data => {
      if (data) {
        setPolling(false)
        const link = document.createElement('a')
        link.href = data.catalogSearchDownloadSignedUrl.signedUrl
        link.click()
      }
    },
    // on error
    error => {
      // if a different error, then something else is wrong
      if (error?.graphQLErrors[0]?.extensions?.code !== 'NOT_FOUND') {
        setPolling(false)
        notifyError(i18n.downloadingTimeout, 'modern', errorNotificationTime)
        return
      }

      setPollCount(currentCount => {
        if (currentCount * pollInterval >= maxPoll) {
          notifyError(i18n.downloadingTimeout, 'modern', errorNotificationTime)
          setPolling(false)
        }
        return currentCount + 1
      })
    }
  )

  useEffect(
    () =>
      // unmount cleanup
      () => {
        setPolling(false)
        setTriggerDownload(false)
      },
    []
  )

  return (
    <React.Fragment>
      <Prompt when={polling || triggerDownload} message={i18n.downloadingBlock} />
      <SheetDropdown>
        <SheetDropdownDisclosureBase css={dropdownButtonStyle}>
          <Button css={downloadReportButtonCss} variant="primary">
            {selectedCount !== 0 ? <span css={selectedCountStyle}>{selectedCount}</span> : null}
            Download
            <DownloadIcon css={downloadReportSvgCss} />
          </Button>
        </SheetDropdownDisclosureBase>

        <Portal>
          <ActionSheetDropdown description="Dropdown">
            <ActionSheetDropdownItem
              onClick={() => onDownload(true)}
              disabled={polling || triggerDownload}
            >
              {i18n.downloadProduct}
            </ActionSheetDropdownItem>
            <ActionSheetDropdownItem
              onClick={() => onDownload(false)}
              disabled={polling || triggerDownload}
            >
              {i18n.downloadItem}
            </ActionSheetDropdownItem>
          </ActionSheetDropdown>
        </Portal>
      </SheetDropdown>
    </React.Fragment>
  )
}
